import { DevTool } from '@hookform/devtools'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import type { ReactElement } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import type { IClusterFormPayload, TCategoryId, TClusterFormField, TFormCategory } from '~/types/cluster'

import Button from '~/components/Buttons/Button'
import MwMLoader from '~/components/MwMLoader'
import { keys as clustersKeys, useClustersAllFormsQuery, usePartialSaveInfosMutation } from '~/queries/clusters'
import { getFormStatusEmoji, getFormTabs, getFormValuesfromFormData, renderFormContent } from '~/utils/clusters'
import { displayToast } from '~/utils/toast'

import './styles.scss'

export type FormValues = Record<string, unknown>

interface Props {
  mutationParams: IClusterFormPayload
}

const InfosForm = ({ mutationParams }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { clusterId, deviceType, storeId } = mutationParams

  const [selectedCategory, setSelectedCategory] = useState<TCategoryId>()

  /** Queries & Mutations */

  const { data: formsData, isLoading } = useClustersAllFormsQuery({
    clusterId,
    deviceType,
    storeId,
  })

  const { mutateAsync: saveInfos } = usePartialSaveInfosMutation()

  /** Query dependent state */

  const methods = useForm<FormValues>({
    values: getFormValuesfromFormData(formsData?.form),
  })

  const {
    control,
    formState: { dirtyFields, isDirty, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = methods

  const categories = getFormTabs(formsData?.status, formsData?.tab_orders)

  const getDirtyValues = (dirtyFields: object | boolean, allValues: object): object => {
    if (dirtyFields === true || Array.isArray(dirtyFields)) return allValues
    return Object.fromEntries(
      //@ts-expect-error Key index typing
      Object.keys(dirtyFields).map((key) => [key, getDirtyValues(dirtyFields[key], allValues[key])])
    )
  }

  /** Handlers */

  const handleCategoryClick = (category: TCategoryId) => () => setSelectedCategory(category)

  const handleCancelClick = () => reset(getFormValuesfromFormData(formsData?.form))

  const handleFormSubmit: SubmitHandler<FormValues> = async (values: FormValues) => {
    if (!!formsData) {
      try {
        const formattedValuesToSend: TClusterFormField[] = Object.entries(getDirtyValues(dirtyFields, values)).map(
          (el) => ({
            question_id: el[0],
            value: el[1],
          })
        )

        if (clusterId && deviceType && storeId) {
          await saveInfos({
            clusterId,
            deviceType,
            fields: formattedValuesToSend,
            storeId,
          })
          reset(getFormValuesfromFormData(values as TFormCategory))
          await queryClient.invalidateQueries({
            queryKey: clustersKeys.allForms({ clusterId, deviceType, storeId }),
          })
          displayToast('App infos succesfully saved', 'success')
        }
      } catch (e) {
        captureException(e)
        displayToast('App infos could not be saved', 'error')
      }
    }
  }

  /** Effects */

  useEffect(() => {
    if (formsData && formsData.tab_orders.length > 0) {
      setSelectedCategory(formsData.tab_orders[0])
    }
  }, [formsData])

  /** Render */

  return (
    <>
      <div className="clusters-app-infos-form-component">
        <FormProvider {...methods}>
          <div className="tabs-wrapper">
            {categories?.map(({ label, value }) => (
              <button
                className={classNames('tab-button', {
                  'tab-button--active': selectedCategory === label,
                })}
                key={`${label}-${value}`}
                onClick={handleCategoryClick(label as TCategoryId)}
              >
                <span className="tab-button__status">{getFormStatusEmoji(value)}</span>
                {label
                  .split('_')
                  .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
                  .join(' ')}
              </button>
            ))}
          </div>
          <form className="form" onSubmit={handleSubmit(handleFormSubmit)}>
            {isLoading ? (
              <MwMLoader category="item" />
            ) : (
              <>
                {renderFormContent({
                  control,
                  data: formsData?.form,
                  register,
                  selectedCategory,
                })}
                <div className="footer">
                  <Button className="cancel-button" ghost onClick={handleCancelClick}>
                    Cancel
                  </Button>
                  <Button disabled={!isDirty || isSubmitting} type="submit">
                    Save
                  </Button>
                </div>
              </>
            )}
          </form>
          <DevTool control={control} />
        </FormProvider>
      </div>
    </>
  )
}

export default InfosForm
